import { carsService } from '@/api/cars.service';
import type {
  Car,
  CreateCarDto,
  FindAllCarsQueryDto,
  UpdateCarDto,
} from '@/entities/cars/car.entity';
import type { RegistrationCertificateExtractionDto } from '@carvia/ros-client-types';
import { addItemToQueriesData, updateItemOnQueriesData } from '@/utils/update-query-data';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef, toValue } from '@vueuse/core';
import type { MutationDefaultsContext } from './query-client';
import { computed } from 'vue';

const QUERY_KEY = 'cars';

export const useCar = (
  id: MaybeRefOrGetter<string | undefined>,
  enabled?: MaybeRefOrGetter<boolean>,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, toRef(id)],
    queryFn: () => carsService.getOne(toValue(id)!),
    enabled: computed(() => toValue(enabled) && !!toValue(id)),
  });
};

export const useCarsWithQuery = (
  query: MaybeRefOrGetter<FindAllCarsQueryDto>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: [QUERY_KEY, toRef(query)],
    queryFn: ({ pageParam = 1 }) =>
      carsService.getAllWithQuery({ ...toValue(query), page: pageParam }),
    enabled: toRef(enabled),
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    initialPageParam: 1,
  });
};

export const useUpdateCar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: ({ id, car }: { id: string; car: UpdateCarDto }) => carsService.update(id, car),
    onMutate: () => ({
      successMessage: 'Car updated',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      updateItemOnQueriesData<Car>([QUERY_KEY], result, queryClient);
    },
  });
};

export const useExtractRegistrationCertificate = () => {
  return useMutation<RegistrationCertificateExtractionDto, Error, string>({
    mutationKey: ['extract-registration-certificate'],
    mutationFn: (carId: string) => carsService.extractRegistrationCertificate(carId),
    onMutate: () => ({ showSuccessToast: false }),
    // Errors are surfaced inline in the form — suppress the global error toast.
    onError: (error) => {
      (error as any).__handled = true;
    },
  });
};

export const useCreateCar = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [QUERY_KEY],
    mutationFn: (car: CreateCarDto) => carsService.create(car),
    onMutate: (): MutationDefaultsContext => ({
      successMessage: 'Car created',
      invalidateQueryKeys: null,
    }),
    onSuccess: (result, variables, onMutateResult, context) => {
      queryClient.getMutationDefaults([])?.onSuccess?.(null, null, onMutateResult, context);
      addItemToQueriesData<Car>([QUERY_KEY], result, queryClient);
    },
  });
};
